import { API_ORIGIN, setToken } from "./index";

/**
 * Better Auth's REST surface, typed by hand.
 *
 * Deliberately not the generated client: these are five endpoints, they are already
 * verified working against the deployed Worker, and a hand-written module keeps every
 * network call in `lib/api/` where the architecture linter can see it.
 *
 * Bearer tokens rather than cookies — the console is on app.micropod.dev and the API on
 * api.micropod.dev, so a cookie would need parent-domain scope and CSRF handling for no
 * benefit. See docs/07-stack-2026.md §3.
 */

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
}

export interface Session {
  user: SessionUser;
  activeOrganizationId: string | null;
}

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    /** The HTTP status, or 0 when the request never got an answer. */
    readonly status: number = 0,
  ) {
    super(message);
  }
}

/**
 * The API could not be reached, or answered with a server error.
 *
 * Deliberately its own type. A signed-in user whose network hiccups was being shown the
 * sign-in screen — `getSession` caught everything and returned null, and null reads as
 * "signed out". Losing your place in an inbox because a request timed out is the kind of
 * bug that makes people distrust a tool, and `docs/03` §9 already says an outage must
 * never render as an empty state. Rendering it as *logged out* is worse than empty.
 */
export class ApiUnreachable extends Error {
  constructor(cause?: unknown) {
    // `cause` is a real Error field, so it is passed rather than shadowed — the original
    // network failure stays attached for anyone debugging from a report.
    super("We could not reach Micropod.", { cause });
  }
}

async function call<T>(path: string, init: RequestInit & { token?: string } = {}): Promise<T> {
  const { token, ...rest } = init;

  let res: Response;
  try {
    res = await fetch(`${API_ORIGIN}/api/auth/${path}`, {
      ...rest,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...rest.headers,
      },
    });
  } catch (err) {
    // The request never left, or never came back. Nothing is known about the session.
    throw new ApiUnreachable(err);
  }

  // 5xx is our fault, not the credential's. Treating it as a rejected login would sign
  // someone out because a Worker cold-started badly.
  if (res.status >= 500) throw new ApiUnreachable();

  const json = (await res.json().catch(() => null)) as Record<string, unknown> | null;

  if (!res.ok) {
    const message = typeof json?.message === "string" ? json.message : "That did not work.";
    const code = typeof json?.code === "string" ? json.code : null;
    throw new AuthError(message, code, res.status);
  }
  return json as T;
}

export async function signUp(input: { email: string; password: string; name: string }) {
  const r = await call<{ token: string; user: SessionUser }>("sign-up/email", {
    method: "POST",
    body: JSON.stringify(input),
  });
  setToken(r.token);
  return r;
}

export async function signIn(input: { email: string; password: string }) {
  const r = await call<{ token: string; user: SessionUser }>("sign-in/email", {
    method: "POST",
    body: JSON.stringify(input),
  });
  setToken(r.token);
  return r;
}

/**
 * Who is signed in.
 *
 * `null` means genuinely signed out — no token, or one the server rejected. It throws
 * `ApiUnreachable` when the answer is unknown, and the caller has to decide what to do
 * with that rather than being handed a `null` that looks identical to a rejection.
 */
export async function getSession(token: string): Promise<Session | null> {
  try {
    const r = await call<{
      user: SessionUser;
      session: { activeOrganizationId: string | null };
    } | null>("get-session", { token });
    return r?.user
      ? { user: r.user, activeOrganizationId: r.session?.activeOrganizationId ?? null }
      : null;
  } catch (err) {
    if (err instanceof ApiUnreachable) throw err;
    // Anything the server actually answered — 401, 403, a malformed session — is a
    // rejection, and a rejection is what being signed out looks like.
    return null;
  }
}

export async function listOrganizations(token: string): Promise<Organization[]> {
  return call<Organization[]>("organization/list", { token });
}

/**
 * One organization is one tenant. There is no second tenancy mechanism.
 *
 * `metadata` carries what only the browser knows — the country the owner picked and the
 * IANA zone their machine is actually in. The server derives locale, currency and above
 * all REGION from the country; none of those are accepted from here, because a region is
 * fixed at database creation and cannot be corrected afterwards.
 */
export async function createOrganization(
  token: string,
  input: { name: string; slug: string; country: string },
): Promise<Organization> {
  return call<Organization>("organization/create", {
    method: "POST",
    token,
    body: JSON.stringify({
      name: input.name,
      slug: input.slug,
      metadata: {
        country: input.country,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }),
  });
}

export async function setActiveOrganization(token: string, organizationId: string): Promise<void> {
  await call("organization/set-active", {
    method: "POST",
    token,
    body: JSON.stringify({ organizationId }),
  });
}

/**
 * Sign out, on the server as well as in this tab.
 *
 * Dropping the local token used to be the whole implementation, which left the session
 * row valid until it expired — so the token in a shared machine's localStorage, or in a
 * browser history entry, still worked after the person believed they had left. The local
 * token is cleared either way: a network failure must not trap someone in a session they
 * asked to end.
 */
export async function signOut(token: string | null): Promise<void> {
  try {
    if (token) await call("sign-out", { method: "POST", token });
  } catch {
    // Best effort. The server may already have expired it, or be unreachable.
  } finally {
    setToken(null);
  }
}
